import { useState, useCallback } from 'react';
import campaignService from '../services/CampaignService';
import aiService from '../services/AIService';

const useMobileStrategy = () => {
  // State management
  const [strategyData, setStrategyData] = useState(null);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [strategySaved, setStrategySaved] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mediaFiles, setMediaFiles] = useState({ pdf: [], image: [] });
  const [strategyImages, setStrategyImages] = useState([]);
  const [campaignData, setCampaignData] = useState(null);

  // Utility functions
  const logInfo = (message, data = null) => {
    console.log(`[useMobileStrategy] ${message}`, data || '');
  };

  const logError = (message, error = null) => {
    console.error(`[useMobileStrategy] ERROR: ${message}`, error || '');
  };

  const setErrorState = (error) => {
    const errorMessage = error?.message || error || 'An unknown error occurred';
    setError(errorMessage);
    logError('Setting error state', errorMessage);
  };

  const clearError = () => {
    setError(null);
  };

  /**
   * Load strategy data from a campaign
   * @param {string} campaignId - Campaign ID
   */
  const loadStrategyFromCampaign = useCallback(async (campaignId) => {
    logInfo('Loading strategy from campaign', { campaignId });
    setLoading(true);
    clearError();

    try {
      const campaign = await campaignService.getCampaign(campaignId);
      
      if (campaign.strategy) {
        // Handle both JSON object and string formats
        let strategyObj;
        if (typeof campaign.strategy === 'string') {
          try {
            strategyObj = JSON.parse(campaign.strategy);
          } catch {
            // If parsing fails, create basic structure
            strategyObj = {
              desc: campaign.strategy,
              weeks: []
            };
          }
        } else {
          strategyObj = campaign.strategy;
        }

        // Ensure weeks array exists
        if (!Array.isArray(strategyObj.weeks)) {
          strategyObj.weeks = [];
        }

        // Map backend posts into strategy weeks (if available)
        if (Array.isArray(campaign.posts) && campaign.posts.length > 0) {
          const mappedWeeks = [...strategyObj.weeks];
          campaign.posts.forEach((post) => {
            const weekIndex = typeof post.week_index === 'number' ? post.week_index : 0;
            // Ensure the week exists
            if (!mappedWeeks[weekIndex]) {
              mappedWeeks[weekIndex] = { desc: '', start_date: null, end_date: null, posts: [] };
            }
            if (!Array.isArray(mappedWeeks[weekIndex].posts)) {
              mappedWeeks[weekIndex].posts = [];
            }
            mappedWeeks[weekIndex].posts.push(post);
          });
          strategyObj.weeks = mappedWeeks;
        }

        setStrategyData(strategyObj);
        logInfo('Strategy loaded successfully', strategyObj);
      } else {
        setStrategyData(null);
        logInfo('No strategy found in campaign');
      }

      // Load media files (pdf, images) if present
      const mediaFilesData = campaign.media_files || campaign.files || campaign.attachments || campaign.documents || campaign.uploads || [];
      if (Array.isArray(mediaFilesData) && mediaFilesData.length > 0) {
        setMediaFiles({
          pdf: mediaFilesData.filter(file => 
            (file.file_type === 'pdf') || 
            (file.type === 'pdf') || 
            (file.mime_type && file.mime_type.includes('pdf')) ||
            (file.filename && file.filename.toLowerCase().endsWith('.pdf'))
          ) || [],
          image: mediaFilesData.filter(file => 
            (file.file_type === 'image') || 
            (file.type === 'image') || 
            (file.mime_type && file.mime_type.startsWith('image/')) ||
            (file.filename && /(jpg|jpeg|png|gif|webp|svg)$/i.test(file.filename))
          ) || []
        });
      } else {
        setMediaFiles({ pdf: [], image: [] });
      }

      // Load strategy/style guide images if present
      const strategyImagesData = campaign.style_guide || campaign.strategy_images || campaign.visual_guide || [];
      if (Array.isArray(strategyImagesData) && strategyImagesData.length > 0) {
        const mappedImages = strategyImagesData.map(img => ({
          url: img.image_url,
          file_url: img.image_url,
          src: img.image_url,
          is_active: img.is_active !== undefined ? img.is_active : true,
          image_alt: img.image_alt || 'Generated style guide image',
          filename: img.filename,
          created_at: img.created_at,
          original_prompt: img.original_prompt,
          revised_prompt: img.revised_prompt
        }));
        setStrategyImages(mappedImages);
      } else {
        setStrategyImages([]);
      }

      return campaign.strategy;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Generate new strategy for campaign using AI
   * @param {string} campaignId - Campaign ID
   * @param {Object} strategyOptions - Strategy generation options
   */
  const generateStrategy = useCallback(async (campaignId, strategyOptions = {}) => {
    logInfo('Generating new strategy', { campaignId, strategyOptions });
    setIsGeneratingStrategy(true);
    clearError();

    try {
      const response = await aiService.generateStrategy(campaignId, strategyOptions);
      
      if (response.strategy) {
        setStrategyData(response.strategy);
        setStrategySaved(true);
        logInfo('Strategy generated successfully', response.strategy);
        return response.strategy;
      } else {
        throw new Error('Invalid strategy response from AI service');
      }
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setIsGeneratingStrategy(false);
    }
  }, []);

  /**
   * Regenerate strategy for specific week
   * @param {string} campaignId - Campaign ID
   * @param {number} weekIndex - Week index (0-based)
   * @param {Object} regenerateOptions - Regeneration options
   */
  const regenerateWeekStrategy = useCallback(async (campaignId, weekIndex, regenerateOptions = {}) => {
    logInfo('Regenerating week strategy', { campaignId, weekIndex, regenerateOptions });
    setLoading(true);
    clearError();

    try {
      const response = await campaignService.regenerateWeekStrategy(campaignId, weekIndex, regenerateOptions);
      
      if (response.week) {
        // Update the specific week in current strategy data
        setStrategyData(prevStrategy => {
          if (!prevStrategy || !prevStrategy.weeks) return prevStrategy;
          
          const updatedWeeks = [...prevStrategy.weeks];
          updatedWeeks[weekIndex] = response.week;
          
          return {
            ...prevStrategy,
            weeks: updatedWeeks
          };
        });
        
        logInfo('Week strategy regenerated successfully', response.week);
        return response.week;
      } else {
        throw new Error('Invalid week regeneration response');
      }
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save strategy to campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} strategy - Strategy data to save
   */
  const saveStrategy = useCallback(async (campaignId, strategy = strategyData) => {
    logInfo('Saving strategy to campaign', { campaignId, strategy });
    setLoading(true);
    clearError();

    try {
      if (!strategy) {
        throw new Error('No strategy data to save');
      }

      const response = await campaignService.updateCampaign(campaignId, {
        strategy: strategy
      });
      
      setStrategySaved(true);
      logInfo('Strategy saved successfully', response);
      return response;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [strategyData]);

  /**
   * Update campaign parameters that affect strategy
   * @param {string} campaignId - Campaign ID
   * @param {Object} params - Parameters to update
   */
  const updateCampaignParameters = useCallback(async (campaignId, params) => {
    logInfo('Updating campaign parameters', { campaignId, params });
    setLoading(true);
    clearError();

    try {
      const response = await campaignService.updateCampaign(campaignId, params);
      logInfo('Campaign parameters updated successfully', response);
      return response;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Start post creation process from strategy
   * @param {string} campaignId - Campaign ID
   * @param {Object} postOptions - Post generation options
   */
  const startPostCreation = useCallback(async (campaignId, postOptions = {}) => {
    logInfo('Starting post creation from strategy', { campaignId, postOptions });
    setLoading(true);
    clearError();

    try {
      const response = await aiService.generatePosts(campaignId, postOptions);
      logInfo('Posts created successfully', response);
      return response;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear strategy data
   */
  const clearStrategy = useCallback(() => {
    logInfo('Clearing strategy data');
    setStrategyData(null);
    setStrategySaved(false);
    clearError();
    setMediaFiles({ pdf: [], image: [] });
    setStrategyImages([]);
    setCampaignData(null);
  }, []);

  /**
   * Check if strategy has unsaved changes
   */
  const hasUnsavedChanges = useCallback(() => {
    return strategyData && !strategySaved;
  }, [strategyData, strategySaved]);

  return {
    // State
    strategyData,
    isGeneratingStrategy,
    strategySaved,
    error,
    loading,
    mediaFiles,
    strategyImages,
    campaignData,

    // Actions
    loadStrategyFromCampaign,
    generateStrategy,
    regenerateWeekStrategy,
    saveStrategy,
    updateCampaignParameters,
    startPostCreation,
    clearStrategy,
    clearError,

    // Computed state
    hasStrategy: !!strategyData,
    hasWeeks: strategyData?.weeks?.length > 0,
    isProcessing: loading || isGeneratingStrategy,
    hasUnsavedChanges: hasUnsavedChanges(),
    
    // Strategy helpers
    getWeekCount: () => strategyData?.weeks?.length || 0,
    getWeek: (index) => strategyData?.weeks?.[index] || null,
    getTotalPosts: () => {
      if (!strategyData?.weeks) return 0;
      return strategyData.weeks.reduce((total, week) => total + (week.posts?.length || 0), 0);
    }
  };
};

export default useMobileStrategy;